import { useAtomValue } from "jotai";
import dayjs from "dayjs";
import { Box, Card, CardContent, Chip, Rating, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { greyDarkerState, greyLighterState, ortexColorState, textColorState, themeState } from "@/lib/store";
import { classNames, dateParser } from "@/lib/utils";
import StarIcon from "@mui/icons-material/Star";
import styles from "./DataGridNews.module.scss";
import { ratingPrecision } from "@/lib/ratingPrecision";

const NewsCard = ({ news, showStock, obfuscated, mobile_design, header_layout, getNewsDetail, sourceTooltip, setSourceTooltip, ratingTooltip, setRatingTooltip }) => {
    const greyLighter = useAtomValue(greyLighterState);
    const greyDarker = useAtomValue(greyDarkerState);
    const textColor = useAtomValue(textColorState);
    const ortexColor = useAtomValue(ortexColorState);
    const theme = useAtomValue(themeState);
    const isMobile = useMediaQuery("(max-width: 768px)");

    const { newsId, publishedAt, source, newsImportance, stocks, title } = news;
    const formattedDate = dateParser(dayjs(publishedAt), "D MMM YYYY [at] HH:mm");
    // Top-importance amber: #faaf00 is 1.9:1 on white, so light mode uses a darker gold (4.7:1).
    const color = newsImportance / 2 === 5 ? (theme === "dark" ? "#faaf00" : "#9a6b00") : theme === "dark" ? greyLighter : greyDarker;
    const sourceLabel = <Chip size="small" label={source[0]} variant="outlined" sx={{ borderColor: color, color: color }} />;

    const stocksContent = showStock && stocks && stocks.length > 0 && (
        <Box className={styles.stocks}>
            {stocks.slice(0, 2).map((stock, index) => (
                <Tooltip key={index} title={obfuscated ? "" : stock.name} arrow placement="bottom-start">
                    <Chip size="small" label={stock.ticker} variant="filled" sx={{ borderColor: ortexColor }} />
                </Tooltip>
            ))}
        </Box>
    );

    return (
        <Card
            key={newsId}
            sx={{
                borderBottom: theme === "dark" ? "1px solid rgb(62, 62, 62)" : "none",
                backgroundColor: "transparent",
                margin: 0,
                marginY: 1,
                width: "99%",
                boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px;",
                marginX: "auto",
                "&:hover": {
                    cursor: obfuscated ? "default" : "pointer",
                },
            }}
            elevation={2}
            onClick={() => getNewsDetail(newsId)}
        >
            <CardContent className={classNames(styles["card-content"], obfuscated && "obfuscated")}>
                <Box className={styles["card-header"]} sx={{ gridTemplateColumns: mobile_design ? "50% 50%" : header_layout }}>
                    {isMobile || mobile_design ? (
                        <>
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", marginBottom: 0.5 }} onMouseEnter={() => setSourceTooltip(news.id)} onMouseLeave={() => setSourceTooltip("")}>
                                <Typography className={styles["header-content"]} style={{ color: theme === "dark" ? greyLighter : greyDarker }}>
                                    {formattedDate}
                                </Typography>
                                <Tooltip arrow title={obfuscated ? "" : source[1]} disableInteractive leaveDelay={100} open={sourceTooltip === news.id} onOpen={() => setSourceTooltip(news.id)} onClose={() => setSourceTooltip("")}>
                                    {sourceLabel}
                                </Tooltip>
                            </Box>
                            <Box className={styles["source-stock"]} sx={{ marginBottom: 0.5 }}>
                                <Tooltip title={obfuscated ? "" : newsImportance / 2} arrow disableInteractive leaveDelay={100} open={ratingTooltip === news.id} onOpen={() => setRatingTooltip(news.id)} onClose={() => setRatingTooltip("")}>
                                    <Box onMouseEnter={() => setRatingTooltip(news.id)} onMouseLeave={() => setRatingTooltip("")}>
                                        <Rating value={newsImportance / 2} readOnly size="small" className={styles.rating} precision={ratingPrecision(newsImportance / 2)} emptyIcon={<StarIcon style={{ color: greyLighter }} fontSize="inherit" />} />
                                    </Box>
                                </Tooltip>
                                {stocksContent}
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="newsCardDate" sx={{ color: theme === "dark" ? greyLighter : greyDarker }}>
                                {formattedDate}
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "flex-start", width: "fit-content" }}>
                                <Box className={styles["source-stock"]} sx={{ width: "fit-content" }} onMouseEnter={() => setSourceTooltip(news.id)} onMouseLeave={() => setSourceTooltip("")}>
                                    {stocksContent}
                                    <Tooltip title={obfuscated ? "" : source[1]} arrow placement="bottom-start" open={sourceTooltip === news.id} onOpen={() => setSourceTooltip(news.id)} onClose={() => setSourceTooltip("")}>
                                        {sourceLabel}
                                    </Tooltip>
                                </Box>
                                <Tooltip title={obfuscated ? "" : newsImportance / 2} arrow disableInteractive leaveDelay={100} open={ratingTooltip === news.id} onOpen={() => setRatingTooltip(news.id)} onClose={() => setRatingTooltip("")}>
                                    <Box onMouseEnter={() => setRatingTooltip(news.id)} onMouseLeave={() => setRatingTooltip("")}>
                                        <Rating value={newsImportance / 2} readOnly size="small" className={styles.rating} precision={ratingPrecision(newsImportance / 2)} emptyIcon={<StarIcon style={{ color: greyLighter }} fontSize="inherit" />} />
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}
                </Box>
                <Box sx={{ color: textColor }}>
                    <Typography variant="body2">{title}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

export default NewsCard;
